import {
  AdAccountPlatform,
  AdAccountResource,
  adAccountUsesApiKey,
  adDataStreamSourceForPlatform,
  DiscoveredAdAccount,
  validateAdAccountId,
} from "@arrowsup/platform-dtos";
import CampaignIcon from "@mui/icons-material/Campaign";
import {
  Box,
  Button,
  List as MuiList,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import {
  BooleanInput,
  BulkDeleteButton,
  Create,
  Datagrid,
  DateInput,
  Edit,
  FunctionField,
  List,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useCreate,
  useNotify,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { useAdPlatformsService } from "../../logic/ad-platforms/ad-platforms.service";
import { apiErrorMessage } from "../../logic/api/api-error-message";
import { omit } from "../../logic/omit";
import { CrudPermissions } from "../auth/crud-permissions";
import {
  ApiKeyInput,
  connectedAccountChanges,
  connectedAccountStatusColumns,
  ConnectedAccountStatusInput,
  ReplaceApiKeyInput,
  VerifyAccessButton,
} from "../connected-accounts/connected-account-fields";
import { TopHelpInfoText } from "../help/top-help-info-text";
import {
  adAccountPlatformChoices,
  defaultAdDataStartDate,
  platformLabel,
} from "./ad-platform-fields";

export const AdAccountResourceView = (perms: CrudPermissions) => (
  <Resource
    name="ad-account"
    key="ad-account"
    options={{ label: "Ad Accounts" }}
    list={<AdAccountList perms={perms} />}
    create={perms.create ? AdAccountCreate : undefined}
    edit={perms.update ? AdAccountEdit : undefined}
    icon={CampaignIcon}
  />
);

const AdAccountList: React.FC<{ perms: CrudPermissions }> = ({ perms }) => (
  <>
    <TopHelpInfoText text="Google Ads, Meta, Microsoft Ads and OpenAI Ads accounts that belong to this account. Arrows Up reaches them through its own manager accounts, or an OpenAI ad account's own API key, and pulls their ad data with ad data streams." />
    <List>
      <Datagrid
        rowClick={perms.update ? "edit" : false}
        bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
      >
        <TextField source="name" label="Name" />
        <FunctionField
          label="Platform"
          render={(r: AdAccountResource) => platformLabel(r.platform)}
        />
        <TextField source="externalId" label="Ad Account ID" />
        <TextField source="currencyCode" label="Currency" />
        <TextField source="timeZone" label="Time Zone" />
        {connectedAccountStatusColumns()}
      </Datagrid>
    </List>
  </>
);

const validateExternalId = (
  value: string | undefined,
  values: { platform?: AdAccountPlatform }
) =>
  values.platform
    ? validateAdAccountId(values.platform, value, { required: true })
    : undefined;

/** Lists the ad accounts Arrows Up can reach, to fill in the ID. */
const DiscoveredAccounts: React.FC = () => {
  const service = useAdPlatformsService();
  const { setValue } = useFormContext();
  const platform = useWatch<{ platform?: AdAccountPlatform }>({
    name: "platform",
  });
  const [accounts, setAccounts] = useState<DiscoveredAdAccount[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  if (!platform) {
    return null;
  }

  const load = async () => {
    setLoading(true);
    setError(undefined);
    try {
      setAccounts(await service.discover(platform));
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Button onClick={() => void load()} disabled={loading}>
        {loading
          ? "Loading…"
          : `Find ${platformLabel(platform)} accounts Arrows Up can reach`}
      </Button>
      {error && <Typography color="error">{error}</Typography>}
      {accounts && accounts.length === 0 && (
        <Typography>No reachable accounts found.</Typography>
      )}
      {accounts && accounts.length > 0 && (
        <MuiList dense sx={{ maxHeight: 300, overflow: "auto" }}>
          {accounts.map((a) => (
            <ListItemButton
              key={a.externalId}
              disabled={a.registeredHere || a.registeredElsewhere}
              onClick={() =>
                setValue("externalId", a.externalId, { shouldValidate: true })
              }
            >
              <ListItemText
                primary={`${a.name ?? "Unnamed"} (${a.externalId})`}
                secondary={
                  a.registeredHere
                    ? "Already added"
                    : a.registeredElsewhere
                    ? "Registered to another account"
                    : [a.currencyCode, a.timeZone].filter(Boolean).join(" · ")
                }
              />
            </ListItemButton>
          ))}
        </MuiList>
      )}
    </Box>
  );
};

/**
 * The ad account's API key on platforms that use one, which finds its ID;
 * otherwise its ID, typed or picked from the ones Arrows Up can reach.
 */
const AccountIdentity: React.FC = () => {
  const platform = useWatch<{ platform?: AdAccountPlatform }>({
    name: "platform",
  });

  if (platform && adAccountUsesApiKey(platform)) {
    return (
      <ApiKeyInput
        helperText={`The ${platformLabel(
          platform
        )} API key for this ad account, from Ads Manager's settings. Arrows Up finds the ad account from it.`}
      />
    );
  }

  return (
    <>
      <DiscoveredAccounts />
      <TextInput
        source="externalId"
        label="Ad Account ID"
        helperText="Pick one above, or type it. Dashes (Google) and an act_ prefix (Meta) are fine. Arrows Up checks it can reach the account before adding it."
        validate={validateExternalId}
        fullWidth
      />
    </>
  );
};

type SyncChoice = { syncAdData?: boolean; syncStartDate?: string };

export const AdAccountCreate: React.FC = () => {
  const [create] = useCreate();
  const notify = useNotify();
  const redirect = useRedirect();
  const syncChoice = useRef<SyncChoice>({});

  const onSuccess = (account: AdAccountResource) => {
    const { syncAdData, syncStartDate } = syncChoice.current;
    if (!syncAdData) {
      notify("Ad account added");
      redirect("list", "ad-account");
      return;
    }
    void create(
      "ad-data-stream",
      {
        data: {
          adAccount: { id: account.id },
          enabled: true,
          source: { name: adDataStreamSourceForPlatform(account.platform) },
          startDate: syncStartDate ?? defaultAdDataStartDate,
        },
      },
      {
        onSuccess: () => {
          notify("Ad account added; its ad data will start syncing");
          redirect("list", "ad-account");
        },
        onError: (e) => {
          notify(
            `Ad account added, but its stream wasn't: ${apiErrorMessage(e)}`,
            {
              type: "warning",
            }
          );
          redirect("list", "ad-account");
        },
      }
    );
  };

  return (
    <Create
      mutationOptions={{ onSuccess }}
      transform={(
        data: Record<string, unknown> &
          SyncChoice & { platform?: AdAccountPlatform }
      ) => {
        syncChoice.current = {
          syncAdData: data.syncAdData,
          syncStartDate: data.syncStartDate,
        };
        const account = omit(data, ["syncAdData", "syncStartDate"]);
        // Arrows Up finds the ID from the key.
        return data.platform && adAccountUsesApiKey(data.platform)
          ? { ...account, externalId: "" }
          : account;
      }}
    >
      <SimpleForm
        defaultValues={{
          syncAdData: true,
          syncStartDate: defaultAdDataStartDate,
        }}
      >
        <SelectInput
          source="platform"
          choices={adAccountPlatformChoices}
          fullWidth
          required
        />
        <AccountIdentity />
        <BooleanInput
          source="syncAdData"
          label="Also sync its ad data"
          helperText="Creates an ad data stream for this ad account."
        />
        <DateInput
          source="syncStartDate"
          label="Sync from"
          helperText="First day to pull. Can't change after the stream is created."
        />
      </SimpleForm>
    </Create>
  );
};

const ApiKey: React.FC = () => {
  const record = useRecordContext<AdAccountResource>();
  return record && adAccountUsesApiKey(record.platform) ? (
    <ReplaceApiKeyInput noun="ad account" />
  ) : null;
};

const fixedFields = [
  "platform",
  "externalId",
  "name",
  "currencyCode",
  "timeZone",
];

const AdAccountVerifyButton: React.FC = () => {
  const service = useAdPlatformsService();
  return <VerifyAccessButton noun="ad account" verify={service.verify} />;
};

export const AdAccountEdit: React.FC = () => (
  <Edit
    redirect="list"
    transform={(data: Record<string, unknown>) =>
      connectedAccountChanges(data, fixedFields)
    }
  >
    <SimpleForm>
      <TextInput source="name" disabled fullWidth />
      <SelectInput
        source="platform"
        choices={adAccountPlatformChoices}
        disabled
        fullWidth
      />
      <TextInput source="externalId" label="Ad Account ID" disabled fullWidth />
      <ApiKey />
      <TextInput source="currencyCode" label="Currency" disabled />
      <TextInput source="timeZone" label="Time Zone" disabled />
      <ConnectedAccountStatusInput whenDisabled="Disabled ad accounts don't sync ad data. Input and output streams that use them still run." />
      <AdAccountVerifyButton />
    </SimpleForm>
  </Edit>
);
